import { AppUtils } from '../../shared/utils/index';
import { PersonalDataModel } from '../models/view/PersonalData';
import { AdresseModel } from '../models/view/Adresse';
import { BasicApplicationService } from './BasicApplicationService';

export interface ViewConfiguration<T extends BasicAppDefinition> {
	data: { [key: string]: any };
	templateurl: string;
	campaignurls?: { [key: string]: any };
	parent: object;
	$ctx: JQuery;
	options: BasicAppOptions<T>;
	callback?: () => void;
	beforeRenderApp?: (template: string) => void;
}

export class BasicViewService<T extends BasicAppDefinition> {
	protected readonly loader: BasicLoader<T>;
	protected readonly options: BasicAppOptions<T>;
	protected readonly parent: StepController;
	protected readonly applicationService: BasicApplicationService<T>;
	protected renderingCanceled: boolean;
	protected readonly $ctx: JQuery;
	protected formId: string;
	protected sectionBgClasses: string[];
	protected autoCompleteZipSelector: string;
	protected autoCompleteStreetSelector: string;
	protected autoCompleteCitySelector: string;
	/**
	 * ViewService constructor
	 */
	constructor(parent: StepController = null, options: BasicAppOptions<T> = null, loader: BasicLoader<T> = null) {
		this.parent = parent;
		this.options = options;
		this.loader = loader;
		this.applicationService = new BasicApplicationService(options);
		this.$ctx = options.$ctx;
		this.formId = options.app.applicationId;
		this.sectionBgClasses = ['h-background'];
		this.autoCompleteZipSelector = '.js-autocomplete-zip';
		this.autoCompleteStreetSelector = '.js-autocomplete-street';
		this.autoCompleteCitySelector = '.js-autocomplete-city';
	}

	public getForm(): JQuery {
		return this.$ctx.find(`#${this.formId} form`);
	}

	public getFormByClassSelector(classSelector = ".js-main-form"): JQuery {
		return this.$ctx.find(`#${this.formId} form${classSelector}`);
	}

	/**
	 * render
	 *
	 * after dependencies are available the plugin is initialized
	 */
	public render(config: ViewConfiguration<T>): void {
		window.addEventListener('beforeunload', () => {
			this.renderingCanceled = true;
		});
		AppUtils.Ajax.fragment(
			{
				url: config.templateurl,
				dataType: 'html',
				timeout: 30000
			},
			(template: string) => {
				this.renderTemplate(template, config);
			},
			(jqxhr: JQuery.jqXHR) => {
				this.handleRenderError(config, jqxhr);
			});
	}

	protected handleRenderError(config: ViewConfiguration<T>, jqxhr: JQuery.jqXHR): void {
		window.setTimeout(() => {
			if (!this.renderingCanceled) {
				AppUtils.Helper.logError(jqxhr, 'ViewService', window.location.href, `GET request from AppUtils.Ajax.fragment ${config.templateurl}`);
				T.Utils.Helper.routeToUrl(this.options.app.errorurl);
			}
		}, 300);
	}

	protected renderTemplate(template: string, config: ViewConfiguration<T>): void {
		if (this.parent && this.parent.beforeRenderApp) {
			this.parent.beforeRenderApp();
		}
		if (config.beforeRenderApp) {
			config.beforeRenderApp(template);
		}
		if (config.callback) {
			const cb = config.callback.bind(config.parent);
			AppUtils.View.renderTemplate(template, config.data, config.$ctx, (): void => {
				cb();
			});
		} else {
			AppUtils.View.renderTemplate(template, config.data, config.$ctx, this._afterRender.bind(this));
		}
	}

	/**
	 * _afterRender.
	 *
	 * the _afterRender function is called when the rendering of the view is finished
	 * in order to event register listeners.
	 * Additionally the application is initialized, so the "ApplicationInitialized" event is dispatched
	 */

	protected _afterRender(): void {
		this.loader.loadSubApps({ options: this.options }, () => {
			//NOSONAR
		});
		this.parent.afterRenderApp();
	}

	/**
	 * Gets the entered personal data from the form.
	 * (only includes input fields, non editable data must be merged from the user model -> see PersonamDataModel.mergeWithUser)
	 *
	 */
	public getPersonalDataModel(): PersonalDataModel {
		return this._getPersonalDataModel(this.getFormData());
	}

	protected _getPersonalDataModel(formData: { [key: string]: any }): PersonalDataModel {
		const personalData = new PersonalDataModel();
		personalData.AnredeKennzeichen = Number(formData.Anrede);
		personalData.Titel = formData.Titel;
		personalData.Vorname = formData.Vorname;
		personalData.Nachname = formData.Nachname;
		personalData.Firma = formData.Firma;
		personalData.Beguenstigter = formData.Beguenstigter;
		personalData.Adresse = new AdresseModel();
		personalData.Adresse.StrasseHausnummer = formData.StrasseHausnummer;
		personalData.Adresse.Postleitzahl = formData.Postleitzahl;
		personalData.Adresse.Ort = formData.Ort;
		personalData.Adresse.Postfachnummer = formData.Postfachnummer;
		personalData.Geburtsdatum = AppUtils.Format.viewDateToIsoDate(formData.Geburtsdatum);
		personalData.Email = formData.Email;
		personalData.TelefonVorwahl = formData.TelefonVorwahl;
		personalData.TelefonNummer = formData.TelefonNummer;
		personalData.TelefonGeschaeftlichVorwahl = formData.TelefonGeschaeftlichVorwahl;
		personalData.TelefonGeschaeftlichNummer = formData.TelefonGeschaeftlichNummer;
		return personalData;
	}

	/**
	 * Set Date by "isodate" on given element
	 */
	public setDate(input: JQuery, isoDate: string): void {
		input.val(AppUtils.Format.isoDateToViewDate(isoDate));
		input.attr('value', AppUtils.Format.isoDateToViewDate(isoDate));
	}

	/**
	 * WARNING: Only use for Insurance begin.
	 * Set Date in Input element, if input element already had a value.
	 * Else it sets the upcoming day as value instead of inputDate.
	 * This method includes a workaround for mobile devices and was provided by EW72.
	 *
	 * Note: actually this code makes no sense at all
	 */
	public setDateToField(input: JQuery, inputDate: string): void {
		input.val(moment(inputDate).format('DD.MM.YYYY'));

		if (AppUtils.Helper.isEmptyString(input.val())) {
			let dateValue = input.attr('value');
			if (dateValue) {
				if (inputDate) {
					dateValue = inputDate;
				}
				const dateValueShortInv = AppUtils.Format.viewDateToShortInvIsoDate(dateValue);
				input.val(dateValueShortInv);
				const inputDateShort = AppUtils.Format.viewDateToShortIsoDate(inputDate);
				input.attr('value', inputDateShort);
				if (input.closest('.l-outer').hasClass('is-touch')) {
					if (!input.closest('.l-outer').hasClass('is-ios')) {
						input.val(inputDateShort);
					}
					input.attr('value', AppUtils.Format.viewDateToShortIsoDate(dateValue));
				}
			}
			// check if value is empty :: chrome needs format yyyy-mm-dd
			else {
				const mDatePlus = moment().add(1, 'd');
				const datePlusShort = AppUtils.Format.viewDateToShortIsoDate(mDatePlus);
				const datePlusShortInv = AppUtils.Format.viewDateToShortInvIsoDate(mDatePlus);
				input.val(datePlusShortInv);
				input.attr('value', datePlusShort);

				if (input.closest('.l-outer').hasClass('is-touch')) {
					input.val(input.closest('.l-outer').hasClass('is-ios') ? datePlusShortInv : datePlusShort);
					input.attr(datePlusShort);
				}
			}
		}
	}

	/**
	 * Shows all dependend elements (with css class 'js-app-depend') that have a data attribute value that matches the provided dependency value and hides all non matching elements.
	 * Elements that do not have a data attribute with the provided data attribute name are ignored. This allows to have several different depenceny groups on the same page.
	 *
	 * @param dependencyValue - the value that must match (ignoring the case) to show the according element.
	 * @param dataAttributeName - the name of the data attribute to compare the provided dependency value with.
	 * @param  removeInsteadOfHide - flag whether to remove non matching elements instead of hiding them. A removed element will not be shown again, if the method is called with a matching dependencyValue.
	 */
	protected showDependendElements(dependencyValue: string, dataAttributeName: string, removeInsteadOfHide: boolean): void {
		this.$ctx.find('.js-app-depend').each(function () {
			const $dependendElement = $(this);
			const attrValue = $dependendElement.data(dataAttributeName);
			if (attrValue) {
				if (attrValue.toUpperCase() === dependencyValue.toUpperCase()) {
					if (!removeInsteadOfHide) {
						$dependendElement.show();
					}
				} else {
					if (!removeInsteadOfHide) {
						$dependendElement.hide();
					} else {
						$dependendElement.remove();
					}
				}
			}
		});
	}

	/**
	 * Returns the serialized form data. The form is expected to be surrounded by a div with id 'app-form' or class 'js-app-form'.
	 *
	 * @return serialized form data.
	 */
	public getFormData(): { [key: string]: any } {
		return AppUtils.Url.getParamsAsCSV(this.getFormElements().serialize());
	}

	/**
	 * Returns the form element(s). The form is expected to be surrounded by a div with id 'app-form' or class 'js-app-form'.
	 *
	 * @return form element(s).
	 */
	protected getFormElements(): JQuery {
		return this.$ctx.find('#app-form > form, .js-app-form form');
	}

	public setFormModelElementsToView(model: { [key: string]: any }): void {
		if (model) {
			this.$ctx.find('input, select').each((key, item: HTMLInputElement) => {
				const itemValue = AppUtils.Helper.getNestedProperty(model, item.name);
				if (itemValue) {
					if (item.type === 'text' || item.type === 'date' || item.type === 'hidden') {
						this.$ctx.find(`input[name="${item.name}"]`).val(itemValue);
					} else if (item.type === 'checkbox') {
						this.$ctx.find(`input[name="${item.name}"][value="${itemValue}"]`).prop('checked', true);
					} else if (item.type === 'select-one') {
						this.$ctx.find(`select[name="${item.name}"] option[value="${itemValue}"]`).prop('selected', true);
					} else if (item.type !== 'text') {
						this.$ctx.find(`input[name="${item.name}"][value="${itemValue}"]`).trigger('click');
					}
				}
			});
		}

	}

	/**
	 * Returns the value of a spcified attribute name
	 */
	public getValueFromViewByAttributeName(attributeName: string): string | number | string[] {
		if (attributeName.length > 0) {
			return this.$ctx.find(`[name=${attributeName}]`).val();
		}
		return undefined;
	}

	public getFormFieldByName(name: string, type = 'input'): JQuery {
		return this.$ctx.find(`${type}[name="${name}"]`);
	}

	public setTaxInformationToView(data: string): void {
		this.$ctx.find(".js-tax-information-text").text(data);
		this.$ctx.find(".js-tax-information").toggleClass("h-hidden", false);
	}

	public getPostedData(key: string): string | number {
		return (this.$ctx.data('postdata') || {})[key];
	}

	public getFromAppContextData(attrName: string): string {
		return `${this.$ctx.data(attrName)}`;
	}

	public showSepaLinkWithInfo(section = ""): void {
		this.$ctx.find(`${section} .js-sepa-downloadnote`).hide();
		this.$ctx.find(`${section} .js-sepa-pdflink`).removeClass('h-hidden');
		this.$ctx.find(`${section} .js-sepa-printnote`).removeClass('h-hidden');
	}

	public toggleCreditCardError(hide: boolean, errorText?: string): void {
		const $creditCardError = this.$ctx.find('.js-cc-error');
		$creditCardError.toggleClass('h-hidden', hide);
		if (!hide) {
			$creditCardError.text(errorText);
			AppUtils.View.scrollToElement($creditCardError);
		}
	}

	public restoreSectionBackground(sectionSelectors: string[], sectionCallback?: (section: JQuery) => void): JQuery {
		if (!sectionSelectors || !sectionSelectors.length) {
			return null;
		}
		const $sections = this.$ctx.find(sectionSelectors.map(value => { return `${value}:visible`; }, []).join(","));
		$sections.each((index, element) => {
			const $section = $(element);
			$section.toggleClass(this.sectionBgClasses.join(" "), 0 === index % 2);
			if (sectionCallback) {
				sectionCallback($section);
			}
		});
		return $sections;
	}

	protected handleAddressAutocompleteSelection(useRedesign = false, e: JQuery.Event, term: any, item: any): void {
		if (item && item.data() && item.length > 0) {
			const itemElem = item.data();

			const allowedTriggers = ["streetandno", "zip", "city"];
			if (!itemElem?.jsId || !allowedTriggers.includes(itemElem.jsId)) {
				return;
			}

			const $street = this.$ctx.find(this.autoCompleteStreetSelector);
			const $city = this.$ctx.find(this.autoCompleteCitySelector);
			const $zip = this.$ctx.find(this.autoCompleteZipSelector);

			$street.val("");
			$street.closest('.ll-row').removeClass("is-valid is-check");

			if (itemElem.jsId === "streetandno") {
				const addressValues = item[0].innerText.split(',');
				if (addressValues.length > 1) {
					const zipCityValue = addressValues[1].split('-');
					$street.val(addressValues[0]).removeClass('is-error');
					$zip.val(zipCityValue[0].trim()).removeClass('is-error');
					$city.val(zipCityValue[1].trim()).removeClass('is-error');
					if (useRedesign) {
						$zip.closest(".ll-row").removeClass("is-error");
						$city.closest(".ll-row").removeClass("is-error");
					}
				}
			}
			else {
				const zipCityValue = item[0].innerText.split('-');
				if (zipCityValue.length > 1) {
					$zip.val(zipCityValue[0].trim()).removeClass('is-error');
					$city.val(zipCityValue[1].trim()).removeClass('is-error');
					if (useRedesign) {
						$zip.closest(".ll-row").removeClass("is-error");
						$city.closest(".ll-row").removeClass("is-error");
					}
				}
			}
		}
	}
}
